import jwtDefaultConfig from './jwtDefaultConfig'

export default class JwtService {
  constructor(axiosIns, jwtOverrideConfig) {
    // Will be used by this service for making API calls
    this.axiosIns = axiosIns
    // jwtConfig <= Will be used by this service
    this.jwtConfig = { ...jwtDefaultConfig, ...jwtOverrideConfig }
    // For Refreshing Token
    this.isAlreadyFetchingAccessToken = false
    // For Refreshing Token
    this.subscribers = []

    // Request Interceptor
    this.axiosIns.interceptors.request.use(
      config => {
        // Get token from localStorage
        const accessToken = this.getToken()

        // If token is present add it to request's Authorization Header
        if (accessToken) {
          // eslint-disable-next-line no-param-reassign
          config.headers.Authorization = `${this.jwtConfig.tokenType} ${accessToken}`
        }
        return config
      },
      error => Promise.reject(error),
    )

    // Add request/response interceptor
    this.axiosIns.interceptors.response.use(
      response => response,
      error => {
        const { config } = error;
        const response = error.response;
        const originalRequest = config;

        // If there is no response (network error, etc.), just reject
        if (!response) {
          return Promise.reject(error);
        }

        // Prevent infinite loop: if the refresh endpoint itself fails with 401, logout
        if (
          response.status === 401 &&
          originalRequest.url === this.jwtConfig.refreshEndpoint
        ) {
          this.deleteToken();
          this.deleteRefreshToken();
          if (typeof this.setUserData === 'function') {
            this.setUserData(null);
          }
          window.location.href = '/';
          return Promise.reject(error);
        }

        if (response.status === 401 && !originalRequest.url.includes('/auth/')) {
          if (!this.isAlreadyFetchingAccessToken) {
            this.isAlreadyFetchingAccessToken = true
            this.axiosIns
              .post(this.jwtConfig.refreshEndpoint, { refreshToken: this.getRefreshToken() })
              .then(r => {
                this.isAlreadyFetchingAccessToken = false
                this.setToken(r.data.access.token)
                this.setRefreshToken(r.data.refresh.token)
                this.onAccessTokenFetched(r.data.access.token)
              });
          }
          const retryOriginalRequest = new Promise(resolve => {
            this.addSubscriber(accessToken => {
              originalRequest.headers.Authorization = `${this.jwtConfig.tokenType} ${accessToken}`
              resolve(this.axiosIns(originalRequest))
            })
          })
          return retryOriginalRequest
        }
        return Promise.reject(error)
      },
    )
  }

  onAccessTokenFetched(accessToken) {
    this.subscribers = this.subscribers.filter(callback => callback(accessToken))
  }

  addSubscriber(callback) {
    this.subscribers.push(callback)
  }

  getToken() {
    return localStorage.getItem(this.jwtConfig.storageTokenKeyName)
  }

  getUserData() {
    const obj = localStorage.getItem(this.jwtConfig.storageUserDataKeyName);
    return JSON.parse(obj);
  }

  getRefreshToken() {
    return localStorage.getItem(this.jwtConfig.storageRefreshTokenKeyName)
  }

  setToken(value) {
    localStorage.setItem(this.jwtConfig.storageTokenKeyName, value)
  }

  setUserData(value) {
    localStorage.setItem(this.jwtConfig.storageUserDataKeyName, JSON.stringify(value))
  }

  setRefreshToken(value) {
    localStorage.setItem(this.jwtConfig.storageRefreshTokenKeyName, value)
  }

  deleteToken() {
    localStorage.removeItem(this.jwtConfig.storageTokenKeyName)
  }

  deleteRefreshToken() {
    localStorage.removeItem(this.jwtConfig.storageRefreshTokenKeyName)
  }

  login(...args) {
    return this.axiosIns.post(this.jwtConfig.loginEndpoint, ...args)
  }

  register(...args) {
    return this.axiosIns.post(this.jwtConfig.registerEndpoint, ...args)
  }

  refreshToken() {
    return this.axiosIns.post(this.jwtConfig.refreshEndpoint, {
      refreshToken: this.getRefreshToken(),
    })
  }
}
